import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Headline, Icon, Text, TextButton, TextLink } from '@oneaudi/unified-web-components';
import { PromotionType } from '../../../types/offers';
import {
  Apr,
  CpoOffer,
  CpoOffersWrapper,
  CreditBonus,
  Divider,
  ExpiryDetails,
  ExpiryTextWrapper,
  Extra,
  MainOffer,
  OfferModalCta,
  PaymentData,
  PaymentsWrapper,
  Percent,
  PercentData,
  SuperScript,
  Term,
} from './VehicleTileCa.styles';
import { rateTransform, numberFormatter } from '../../../utils/formatPrice';
import { Color, COLORS } from '../../../styles/variables';
import { formatOfferPercentage, rangeConverter } from '../../../utils/combineCpoOffers';
import { StyledText } from '../../Common/Common.styles';

interface PaymentOptionsCaProps {
  offer: PromotionType;
  // eslint-disable-next-line react/no-unused-prop-types
  hasSmallFont?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  labelColor?: Color;
  isFeatured?: boolean;
  isModal?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  showOfferDetails?: (e: any) => void;
}

const PaymentOptionsCa: React.FC<PaymentOptionsCaProps> = ({
  offer,
  isFeatured,
  isModal,
  showOfferDetails,
}) => {
  const {
    monthlyPayment,
    term,
    customerDownPayment,
    aprRate,
    offerType,
    offerModelDetail,
    cpoOffers,
    creditBonus,
    offerPlusEn,
    offerPlusFr,
    paymentFreq,
  } = offer || {};
  const { vehicleType } = offerModelDetail || {};
  const { t, i18n } = useTranslation();

  const lang = i18n.language;

  const isFrenchPage = i18n.language === 'fr';

  const isCpoOffer = vehicleType === 'USED-CAR';

  const offerPlus = isFrenchPage ? offerPlusFr : offerPlusEn;

  const listOfAprs = useMemo(() => {
    if (!cpoOffers) return [];
    const listOfKeys = Object.keys(cpoOffers).filter((apr) => cpoOffers[apr] !== null);
    if (isModal) return listOfKeys;
    return listOfKeys.slice(0, 1);
  }, [cpoOffers, isModal]);

  const paymentFrequency = useMemo(() => {
    if (paymentFreq === 'WEEKLY') return t('promo.tile.payments.weeklyPayment');
    if (paymentFreq === 'BI-WEEKLY') return t('promo.tile.payments.biWeeklyPayment');
    return t('promo.tile.payments.monthlyPayment');
  }, [paymentFreq, t]);

  const renderCpoOffers = useMemo(() => {
    if (!cpoOffers) return null;
    if (!listOfAprs) return null;
    return (
      <CpoOffersWrapper isModal={isModal}>
        {listOfAprs.map((apr, index) => {
          const isFirstOrLastOffer = (index === 1 || index === 0) && listOfAprs.length > 1;
          return (
            <>
              <CpoOffer key={apr}>
                <Apr>{t('promo.tile.payments.apr')}</Apr>
                <Percent data-testid={`offer-cpo-apr-${index}`}>
                  <Headline
                    tag="h4"
                    variant={{
                      order: '4',
                      type: 'headline',
                      weight: 'bold',
                    }}
                  >
                    {formatOfferPercentage(cpoOffers[apr])}%
                  </Headline>
                  {!isModal && showOfferDetails && (
                    <SuperScript>
                      <TextLink
                        className="link"
                        onClick={showOfferDetails}
                        size="sm"
                        variant="inline"
                      >
                        1
                      </TextLink>
                    </SuperScript>
                  )}
                </Percent>
                <Term data-testid={`offer-cpo-term-${index}`}>
                  {rangeConverter(apr)} {t('promo.tile.months')}
                </Term>
              </CpoOffer>
              {isFirstOrLastOffer && <Divider />}
            </>
          );
        })}
      </CpoOffersWrapper>
    );
  }, [cpoOffers, isModal, listOfAprs, showOfferDetails, t]);

  const renderCreditBonus = useMemo(() => {
    if (!creditBonus) return null;
    if (isModal) return null;
    return (
      <CreditBonus isFeatured={isFeatured} data-testid="offer-credit-bonus">
        <Icon name="special-offer" size="s" />
        <Text
          variant={{
            order: isFeatured ? '2' : '1',
            style: 'bold',
          }}
        >
          {numberFormatter(creditBonus, lang)} {t('promo.tile.audiCredit')}
        </Text>
      </CreditBonus>
    );
  }, [creditBonus, isFeatured, isModal, lang, t]);

  const renderOfferPlus = useMemo(() => {
    if (!offerPlus) return null;
    if (isModal) return null;
    return (
      <Extra data-testid="offer-offer-plus">
        <Text
          variant={{
            order: '2',
            style: 'normal',
          }}
        >
          {offerPlus}
        </Text>
      </Extra>
    );
  }, [isModal, offerPlus]);

  const renderNewOffers = useMemo(() => {
    return (
      <>
        <MainOffer isModal={isModal}>
          <PercentData isFeatured={isFeatured}>
            <Apr>{t('promo.tile.payments.apr')}</Apr>
            <Percent data-testid="offer-new-apr">
              <Headline
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'bold',
                }}
              >
                {rateTransform(aprRate, i18n.language)}%
              </Headline>
              {!isModal && showOfferDetails && (
                <SuperScript>
                  <TextLink className="link" onClick={showOfferDetails} size="sm" variant="inline">
                    1
                  </TextLink>
                </SuperScript>
              )}
            </Percent>
            <Term data-testid="offer-new-term">
              {t('promo.tile.payments.upTo')} {term} {t('promo.tile.payments.months')}
            </Term>
          </PercentData>
          <Divider />
          <PaymentData isFeatured={isFeatured}>
            <Apr data-testid="offer-new-freq">{paymentFrequency}</Apr>
            <Percent>
              <Headline
                tag="h4"
                variant={{
                  order: '4',
                  type: 'headline',
                  weight: 'bold',
                }}
              >
                {numberFormatter(Number((monthlyPayment ?? 0).toFixed()), lang)}
              </Headline>
              {!isModal && showOfferDetails && (
                <SuperScript>
                  <TextLink className="link" onClick={showOfferDetails} size="sm" variant="inline">
                    1
                  </TextLink>
                </SuperScript>
              )}
            </Percent>
            <Term data-testid="offer-new-downpayment">
              {numberFormatter(customerDownPayment, lang)} {t('promo.tile.payments.downPayment')}
            </Term>
          </PaymentData>
        </MainOffer>
        {renderCreditBonus}
        {renderOfferPlus}
      </>
    );
  }, [
    aprRate,
    customerDownPayment,
    i18n.language,
    isFeatured,
    isModal,
    lang,
    monthlyPayment,
    paymentFrequency,
    renderCreditBonus,
    renderOfferPlus,
    showOfferDetails,
    t,
    term,
  ]);

  const renderExtraCtas = useMemo(() => {
    if (!offer || isModal) return null;
    return (
      <ExpiryDetails>
        <ExpiryTextWrapper>
          <StyledText
            data-testid="model-offer-valid-date"
            variant={{
              type: 'copy',
              order: '2',
              style: 'normal',
            }}
            color={COLORS.grey60}
          >
            {t('promo.tile.offerEnd')} {offer.programToDate}
          </StyledText>
        </ExpiryTextWrapper>
        <OfferModalCta>
          <TextButton
            aria-label="View offer details"
            data-testid="model-cta-offer-details"
            variant="tertiary"
            href="#"
            size="sm"
            onClick={showOfferDetails}
          >
            {t('promo.tile.viewDetails')}
          </TextButton>
        </OfferModalCta>
      </ExpiryDetails>
    );
  }, [isModal, offer, showOfferDetails, t]);

  const renderContent = useMemo(() => {
    if (isCpoOffer) return renderCpoOffers;
    return renderNewOffers;
  }, [renderCpoOffers, renderNewOffers, isCpoOffer]);

  if (!offer) return null;
  if (offerType === 'credit') return null;

  return (
    <PaymentsWrapper isCpoOffer={isCpoOffer} isFeatured={isFeatured}>
      {renderContent}
      {renderExtraCtas}
    </PaymentsWrapper>
  );
};

export default PaymentOptionsCa;
